import React, { useRef, useState, useEffect } from "react";

import { MdCheckCircle } from "react-icons/md";
import { MdCancel } from "react-icons/md";
import { FiEye, FiEyeOff } from "react-icons/fi";

import styles from "./Input.module.scss";

export default function Input({
  type = "",
  placeholder = "",
  value,
  label = "",
  onChange,
  disabled = false,
  onFocused = false,
  min,
  max,
  maxLength,
  required = false,
  valid,
  message = "",
  className = "",
  useBorder = true,
  iconBefore,
  iconAfter,
  clickableIcon = false,
  iconClassName = "",
  prefix,
  suffix,
  style = {},
  rows = 4,
}) {
  const inputRef = useRef(null);
  const isTextarea = type === "textarea";

  const [input, setInput] = useState("");
  const [status, setStatus] = useState({ valid: undefined });
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState(type);

  useEffect(() => {
    // Keep internal state in sync with controlled `value` (including empty string / 0)
    if (value !== undefined) setInput(value ?? "");
  }, [value]);

  useEffect(() => {
    if (onFocused) {
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 0);
    }
  }, [onFocused]);

  useEffect(() => {
    setStatus({
      valid: toBoolean(valid),
    });
  }, [valid]);

  useEffect(() => {
    if (type === "password") {
      setInputType(showPassword ? "text" : "password");
    } else if (type !== "textarea") {
      setInputType(type);
    }
  }, [type, showPassword]);

  const toBoolean = (value) => {
    if (value === undefined) return undefined;
    if (typeof value === "string") return value?.toLowerCase() === "true";
    return value;
  };

  const onChangeInput = (event) => {
    onChange?.(event.target.value);
    setInput(event.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const applyClasses = () => {
    const classes = [];

    if (status?.valid !== undefined) {
      if (status?.valid) classes.push("input-container__input--valid");
      else classes.push("input-container__input--invalid");
    }

    if (iconBefore || prefix)
      classes.push("input-container__input--icon-before");
    if (iconAfter || suffix || type === "password")
      classes.push("input-container__input--icon-after");

    return classes;
  };

  const statusClass = () => {
    if (status?.valid !== undefined) {
      if (status?.valid) return "input-container__message--valid";
      else return "input-container__message--invalid";
    }

    return;
  };

  const onFocus = () => {
    setStatus({
      valid: undefined,
    });
  };

  // Render password toggle icon if input type is password
  const renderPasswordToggle = () => {
    if (type === "password") {
      return (
        <span
          className={`${styles["input-icon"]} ${styles["input-icon--after"]} ${styles["password-toggle"]}`}
          onClick={togglePasswordVisibility}
          style={{ cursor: "pointer", color: "#6c757d" }}
        >
          {showPassword ? <FiEyeOff /> : <FiEye />}
        </span>
      );
    }
    return null;
  };

  return (
    <div
      className={`${styles["input-container"]} ${
        !useBorder && styles["input-container--no-border"]
      }`}
    >
      {label && (
        <label htmlFor={inputRef} className={styles["input-container__label"]}>
          <span>{label}</span>
          {required && (
            <span className={styles["input-container__label--required"]}>
              *
            </span>
          )}
        </label>
      )}
      <div className="u-relative d-flex align-items-center">
        {isTextarea ? (
          <textarea
            className={`${styles["input-container__input"]} ${styles["input-container__textarea"]} ${
              className && className
            } ${applyClasses()?.length ? styles[applyClasses().join(" ")] : ""} `}
            ref={inputRef}
            value={input}
            onChange={(event) => onChangeInput(event)}
            disabled={disabled || false}
            placeholder={placeholder}
            maxLength={maxLength}
            onFocus={(event) => onFocus(event)}
            rows={rows}
            style={style}
          />
        ) : (
          <input
            className={`${styles["input-container__input"]} ${
              className && className
            } ${applyClasses()?.length ? styles[applyClasses().join(" ")] : ""} `}
            type={inputType}
            ref={inputRef}
            value={input}
            onChange={(event) => onChangeInput(event)}
            disabled={disabled || false}
            placeholder={placeholder}
            min={min}
            max={max}
            maxLength={maxLength}
            onFocus={(event) => onFocus(event)}
            style={style}
          />
        )}

        {prefix && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--before"]
            } ${iconClassName !== undefined && iconClassName}`}
          >
            {prefix}
          </span>
        )}

        {iconBefore && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--before"]
            } ${iconClassName !== undefined && iconClassName}`}
            onClick={() => clickableIcon && setInput("")}
          >
            {iconBefore}
          </span>
        )}

        {suffix && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--after"]
            } ${iconClassName !== undefined && iconClassName} ps-5 pe-5`}
          >
            {suffix}
          </span>
        )}

        {iconAfter && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--after"]
            } ${iconClassName !== undefined && iconClassName}`}
            onClick={() => clickableIcon && setInput("")}
          >
            {iconAfter}
          </span>
        )}

        {/* Render password toggle if no suffix is provided */}
        {type === "password" && !suffix && renderPasswordToggle()}

        {status?.valid !== undefined && (
          <span className={styles["input-container__input-icons"]}>
            {status?.valid ? (
              <MdCheckCircle
                className={styles["input-container__input-icons--valid"]}
              />
            ) : (
              <MdCancel
                className={styles["input-container__input-icons--invalid"]}
              />
            )}
          </span>
        )}

        {status?.valid !== undefined && message && (
          <span
            className={`${styles["input-container__message"]} ${
              styles[statusClass()]
            }`}
          >
            {message || "Required field"}
          </span>
        )}
      </div>
    </div>
  );
}
